import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { judgeRange, missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Compression-artefact check.
 *
 * Blockiness is only measurable on the original pixel grid: the analysis buffer
 * is downscaled for speed, which destroys the 8x8 DCT alignment. When that
 * happens the check returns `not_checked` — a fabricated "pass" here would be
 * worse than no answer.
 */
export const compressionCheck: ValidationCheck = {
  id: 'compression_artifacts',
  title: 'Артефакты сжатия',
  description: 'Ищет блочность и «звон» вокруг контуров, характерные для сильного JPEG-сжатия.',
  kind: 'quality',
  requires: ['quality'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['quality']);
    if (missing) return notChecked(ctx, compressionCheck, missing, ctx.rule.severity);
    const quality = ctx.quality;
    if (!quality) return notChecked(ctx, compressionCheck, 'Метрики качества недоступны.', ctx.rule.severity);

    const maxBlockiness = numberParam(ctx.rule.params, 'maxBlockiness', 0.14);
    const maxRinging = numberParam(ctx.rule.params, 'maxRinging', 0.4);
    const { measured, blockiness, ringing } = quality.compression;

    if (!measured) {
      return plainResult({
        id: compressionCheck.id,
        title: compressionCheck.title,
        status: 'not_checked',
        severity: ctx.rule.severity,
        message:
          'Артефакты сжатия не проверены: анализ выполняется на уменьшенной копии, где блочность JPEG измерить нельзя.',
        fixability: 'none',
        details: { reason: 'analysis_buffer_downscaled', maxBlockiness, maxRinging },
      });
    }

    const blockVerdict = judgeRange({ value: blockiness, max: maxBlockiness, warningMargin: 0.2 });
    const ringVerdict = judgeRange({ value: ringing, max: maxRinging, warningMargin: 0.2 });
    const status =
      blockVerdict.status === 'fail' || ringVerdict.status === 'fail'
        ? 'fail'
        : blockVerdict.status === 'warning' || ringVerdict.status === 'warning'
          ? 'warning'
          : 'pass';

    const problems: string[] = [];
    if (blockiness > maxBlockiness * 0.8) problems.push('заметна блочность (квадраты 8×8)');
    if (ringing > maxRinging * 0.8) problems.push('заметны ореолы вокруг контуров');

    return plainResult({
      id: compressionCheck.id,
      title: compressionCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(blockiness, 3),
      max: maxBlockiness,
      message:
        status === 'pass'
          ? 'Следов сильного JPEG-сжатия не обнаружено.'
          : `Изображение сильно сжато: ${problems.join('; ')}.`,
      requirement: 'Не допускается заметное JPEG-сжатие, искажающее черты лица.',
      fixability: 'retake',
      confidence: 0.6,
      details: {
        blockiness: roundTo(blockiness, 4),
        ringing: roundTo(ringing, 4),
        maxBlockiness,
        maxRinging,
        displayValue: roundTo(blockiness * 100, 1).toFixed(1).replace('.', ',') + ' %',
      },
    });
  },
};
